import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Info, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { downloadFile } from '../../lib/download';
import { errorMessage } from '../LoadError';
import { LineStatus, inr } from './PurchaseBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACTION_TONE = { post: 'bg-green-50 text-green-800', draft: 'bg-amber-50 text-amber-800', skip: 'bg-red-50 text-red-800' };
const ACTION_LABEL = { post: 'Will post', draft: 'Saved as draft', skip: 'Skipped' };

const InvoicePreview = ({ invoice, index }) => (
  <div className="rounded-xl border" data-testid={`purchase-upload-invoice-${index}`}>
    <div className="flex flex-wrap items-center gap-3 border-b bg-gray-50 px-3 py-2 text-[12px]">
      <span className={`rounded px-2 py-0.5 font-semibold ${ACTION_TONE[invoice.action]}`} data-testid={`purchase-upload-action-${index}`}>{ACTION_LABEL[invoice.action]}</span>
      <b className="font-mono">{invoice.invoice_no || '(no invoice no.)'}</b><span>{invoice.supplier || '(no supplier)'}</span><span className="text-gray-500">{invoice.invoice_date}</span>
      <span className="ml-auto">{invoice.totals.qty} units · {inr(invoice.totals.amount)} + GST {inr(invoice.totals.gst_amount)} = <b>{inr(invoice.totals.total_incl_gst)}</b></span>
    </div>
    {invoice.problems.length > 0 && <p className="px-3 pt-2 text-[12px] text-red-700">{invoice.problems.join(' · ')}</p>}
    <div className="overflow-x-auto">
      <div className="min-w-[960px] divide-y">
        {invoice.lines.map((line, i) => (
          <div key={`${line.source_row}-${i}`} className="grid grid-cols-[55px_150px_1fr_50px_90px_100px_85px_105px_240px] gap-2 px-3 py-1.5 text-[12px]">
            <span className="text-gray-500">Row {line.source_row}</span><span className="font-mono">{line.sku_code || '–'}</span>
            <span className="truncate text-gray-600">{line.product_name || '–'}</span><span className="text-right">{line.qty ?? '–'}</span>
            <span className="text-right">{line.unit_cost != null ? inr(line.unit_cost) : '–'}</span><span className="text-right">{line.amount != null ? inr(line.amount) : '–'}</span>
            <span className="text-right">{line.gst_amount != null ? inr(line.gst_amount) : '–'}</span>
            <span className="text-right font-semibold" data-testid={`purchase-upload-line-total-${index}-${i}`}>{line.line_total != null ? inr(line.line_total) : '–'}</span>
            <LineStatus line={line} index={`${index}-${i}`} />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const PurchaseUploadDialog = ({ open, onOpenChange, country, isAdmin, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setFile(null); setPreview(null); } }, [open]);

  const send = async (path, chosen) => {
    const body = new FormData();
    body.append('file', chosen);
    body.append('country', country);
    return axios.post(`${API}/purchases/upload/${path}`, body);
  };

  const choose = async chosen => {
    if (!chosen) return;
    setFile(chosen);
    setBusy(true);
    try {
      setPreview((await send('preview', chosen)).data);
    } catch (error) {
      toast.error(`Could not read the file: ${errorMessage(error)}`);
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await send('confirm', file);
      toast.success(res.data.message);
      res.data.failures?.forEach(f => toast.warning(`${f.invoice_no} (${f.supplier}): ${f.error}`));
      onImported();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || errorMessage(error, 'Upload failed'));
    } finally {
      setBusy(false);
    }
  };

  const summary = preview?.summary;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-[1100px] overflow-y-auto rounded-2xl" data-testid="purchase-upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-[17px] font-semibold">Upload purchases</DialogTitle>
          <DialogDescription>One file can hold many invoices. Rows with the same Supplier + Invoice No. become one invoice. Nothing is saved until you confirm.</DialogDescription>
        </DialogHeader>
        {!isAdmin && (
          <div role="note" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900" data-testid="purchase-upload-admin-note">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>Only an admin can confirm an upload. You can still check a file here and fix any problems before passing it to an admin, or enter invoices one at a time with <b>New purchase</b> and save them as drafts for an admin to post.</p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadFile(`${API}/purchases/template`, 'PURCHASES_TEMPLATE.xlsx').catch(() => toast.error('Download failed'))} data-testid="purchase-template-download"><Download className="mr-1 h-4 w-4" />Download template</Button>
          <label className="inline-flex cursor-pointer items-center rounded-md border px-3 py-1.5 text-[13px] hover:bg-gray-50" data-testid="purchase-upload-choose">
            <Upload className="mr-1 h-4 w-4" />{file ? file.name : 'Choose .xlsx / .csv'}
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={e => choose(e.target.files?.[0])} data-testid="purchase-upload-input" />
          </label>
          {busy && <span className="text-[12px] text-gray-500">Working…</span>}
        </div>
        {summary && (
          <>
            <div className="flex flex-wrap gap-4 rounded-xl bg-gray-50 px-4 py-3 text-[13px]" data-testid="purchase-upload-summary">
              <span>{summary.invoices} invoices · {summary.lines} lines</span>
              <span className="text-green-700">{summary.post} will post ({inr(summary.amount)})</span>
              <span className="text-amber-700">{summary.draft} saved as draft</span>
              <span className="text-red-700">{summary.skip} skipped</span>
              <span className="text-red-700">{summary.problem_lines} problem lines</span>
            </div>
            <div className="space-y-3">{preview.invoices.map((invoice, i) => <InvoicePreview key={`${invoice.supplier}-${invoice.invoice_no}-${i}`} invoice={invoice} index={i} />)}</div>
          </>
        )}
        <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="purchase-upload-cancel">{isAdmin ? 'Cancel' : 'Close'}</Button>
          <Button disabled={!summary || busy || !isAdmin || summary.post + summary.draft === 0} onClick={confirm} title={isAdmin ? undefined : 'Only admins can confirm an upload.'}
            className="bg-[#007AFF] text-white hover:bg-[#0066CC]" data-testid="purchase-upload-confirm">
            {isAdmin ? 'Confirm upload' : 'Admins only'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
